import { parseSegmentId } from "./deckModel";

// Turning a segment ID into a live slide or shape.
//
// Its own module because two callers need exactly this and must not disagree: the tool
// executor, which is about to edit whatever it finds, and the citation reveal, which is
// about to select it. A citation that scrolls somewhere different from where the same ID
// was edited would be a quiet and very confusing bug.

/** An expected, explainable failure: reported to the model as `error`, not thrown out. */
export class ToolError extends Error {}

/**
 * A runtime requirement-set check.
 *
 * The manifest floor is PowerPointApi 1.7, so everything up to and including 1.7 can be
 * used without asking. 1.8 brings tables and `Slide.moveTo`, and raising the floor to get
 * them would drop every volume-licensed perpetual install and every iPad -- so those two
 * features are asked for here and degrade with an explanation when the answer is no.
 *
 * Called inside the function, never at module scope: `Office.context` is empty before
 * Office.onReady and the Office global does not exist at all under Jest.
 */
export const supportsApiSet = (version: string): boolean => {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", version);
  } catch {
    return false;
  }
};

/** Parses a segment ID that must name a shape, not just a slide. */
export const requireShapeAddress = (segmentId: string) => {
  const parsed = parseSegmentId(segmentId);
  if (!parsed?.shapeId) {
    throw new ToolError(
      `"${segmentId}" is not a shape ID. A shape ID looks like "s3/sh17" -- slide number, then the shape's own id.`
    );
  }
  return { slideNumber: parsed.slideNumber, shapeId: parsed.shapeId };
};

/** Parses a segment ID that must name a slide. */
export const requireSlideAddress = (segmentId: string): number => {
  const parsed = parseSegmentId(segmentId);
  if (!parsed || parsed.shapeId) {
    throw new ToolError(`"${segmentId}" is not a slide ID. A slide ID looks like "s3".`);
  }
  return parsed.slideNumber;
};

export interface LocatedSlide {
  slide: PowerPoint.Slide;
  /** Zero-based position as the deck stands right now. */
  slideIndex: number;
  slideCount: number;
}

/**
 * Finds a slide by 1-based number, or by its own id when one is supplied.
 *
 * When `expectedSlideId` is given it wins over the number, and a number that disagrees is
 * not an error: the id is the identity and the number is only where the model last saw
 * it. That is the whole reason delete_slide asks for both.
 */
export const locateSlide = async (
  context: PowerPoint.RequestContext,
  slideNumber: number,
  expectedSlideId?: string
): Promise<LocatedSlide> => {
  const slides = context.presentation.slides;
  slides.load("items/id");
  await context.sync();
  const slideCount = slides.items.length;

  if (expectedSlideId) {
    const slideIndex = slides.items.findIndex((slide) => slide.id === expectedSlideId);
    if (slideIndex < 0) {
      throw new ToolError(
        `No slide with slideId "${expectedSlideId}" exists any more. Re-read the presentation state before trying again.`
      );
    }
    return { slide: slides.items[slideIndex], slideIndex, slideCount };
  }

  if (slideNumber < 1 || slideNumber > slideCount) {
    throw new ToolError(
      `Slide ${slideNumber} does not exist; the presentation has ${slideCount} slide(s).`
    );
  }
  return { slide: slides.items[slideNumber - 1], slideIndex: slideNumber - 1, slideCount };
};

export interface LocatedShape {
  shape: PowerPoint.Shape;
  slide: PowerPoint.Slide;
  slideIndex: number;
  shapeType: string;
  /** The shape was not on the slide the segment ID named; this is where it actually is. */
  relocated: boolean;
}

/**
 * Finds a shape by the identity half of its segment ID.
 *
 * The named slide is tried first because that is nearly always right and costs one round
 * trip. If the shape is not there -- the slide was deleted, or slides were reordered
 * earlier in this same batch of tool calls -- the whole deck is searched for that shape
 * id instead. This is the payoff for addressing shapes by PowerPoint's own id rather than
 * by position: the edit still lands on the shape the model meant, and the caller is told
 * it moved so it can say so.
 */
export const locateShape = async (
  context: PowerPoint.RequestContext,
  slideNumber: number,
  shapeId: string
): Promise<LocatedShape> => {
  const slides = context.presentation.slides;
  slides.load("items/id");
  await context.sync();
  const slideCount = slides.items.length;

  if (slideNumber >= 1 && slideNumber <= slideCount) {
    const namedSlide = slides.items[slideNumber - 1];
    const onNamedSlide = namedSlide.shapes.getItemOrNullObject(shapeId);
    onNamedSlide.load("id, name, type");
    await context.sync();
    if (!onNamedSlide.isNullObject) {
      return {
        shape: onNamedSlide,
        slide: namedSlide,
        slideIndex: slideNumber - 1,
        shapeType: String(onNamedSlide.type),
        relocated: false,
      };
    }
  }

  const candidates = slides.items.map((slide) => slide.shapes.getItemOrNullObject(shapeId));
  for (const candidate of candidates) candidate.load("id, name, type");
  await context.sync();

  const foundAt = candidates.findIndex((candidate) => !candidate.isNullObject);
  if (foundAt < 0) {
    throw new ToolError(
      `Shape "${shapeId}" is no longer in this presentation. Re-read the presentation state before trying again.`
    );
  }
  return {
    shape: candidates[foundAt],
    slide: slides.items[foundAt],
    slideIndex: foundAt,
    shapeType: String(candidates[foundAt].type),
    relocated: true,
  };
};

/** "Note: that shape is now on slide 5..." -- appended when a shape had moved. */
export const relocationNote = (located: LocatedShape, namedSlideNumber: number): string =>
  located.relocated
    ? ` Note: that shape is now on slide ${located.slideIndex + 1}, not slide ${namedSlideNumber}.`
    : "";
